import { formatDate, formatTime } from '@fusapp-work/shell'
import type { Personnel } from './domain/personnel'

/**
 * Presentation of the approved Users screens (Console v2.dc.html:689-800, valsU 4644-4653, 4709-4715) over the real
 * personnel projection. Values the backend cannot provide (invitations, lockout, MFA, failed attempts) are shown as an
 * explicit unknown and are never derived or invented.
 */
export type PersonnelStatusKey = 'activeStatus' | 'disabledStatus' | 'unknownStatus'
type StatusPerson = Pick<Personnel, 'status' | 'accessStatus'>

/** `Observed` is the only persisted state that allows access (CosmosPersonnelStatuses.AllowsAccess). */
export function personnelStatusKey(person: StatusPerson): PersonnelStatusKey {
  if (person.status === 'Observed' && person.accessStatus !== 'disabled') return 'activeStatus'
  if (person.status === 'Disabled') return 'disabledStatus'
  return 'unknownStatus'
}

type KpiKey = 'consolePersonnel.kpiTotal' | 'consolePersonnel.rolesDefined' | 'consolePersonnel.unavailable' | 'consolePersonnel.activeStatus'
  | 'consolePersonnel.mfaUnknown' | 'consolePersonnel.kpiInvited' | 'consolePersonnel.inviteUnknown' | 'consolePersonnel.kpiBlocked' | 'consolePersonnel.blockedDetail'
type Translate = (key: KpiKey, named?: Record<string, unknown>, plural?: number) => string
export type KpiTone = 'muted' | 'warning'
export interface PersonnelKpi { readonly id: 'total' | 'active' | 'invited' | 'blocked'; readonly label: string; readonly value: string; readonly sub: string; readonly tone: KpiTone; readonly filter?: string; readonly unknown?: boolean }

/** The four design cards: total, active, pending invitation (D6 gap) and locked · disabled (lock is a D6 gap). */
export function personnelListKpis(counts: { total: number; observed: number; disabled: number }, roleCount: number | undefined, t: Translate): PersonnelKpi[] {
  return [
    { id: 'total', label: t('consolePersonnel.kpiTotal'), value: String(counts.total), sub: roleCount === undefined ? t('consolePersonnel.unavailable') : t('consolePersonnel.rolesDefined', { count: roleCount }, roleCount), tone: 'muted', filter: '' },
    { id: 'active', label: t('consolePersonnel.activeStatus'), value: String(counts.observed), sub: t('consolePersonnel.mfaUnknown'), tone: 'warning', filter: 'Observed' },
    { id: 'invited', label: t('consolePersonnel.kpiInvited'), value: '—', sub: t('consolePersonnel.inviteUnknown'), tone: 'muted', unknown: true },
    { id: 'blocked', label: t('consolePersonnel.kpiBlocked'), value: String(counts.disabled), sub: t('consolePersonnel.blockedDetail', { count: counts.disabled }, counts.disabled), tone: counts.disabled > 0 ? 'warning' : 'muted', filter: 'Disabled' },
  ]
}

/**
 * Design `udKpis[0]`: the day/month is the value and the time is the subline; no recorded sign-in is shown as '—'.
 * A sign-in from another year carries the year, so a stale account never reads like a recent one.
 */
export function lastLoginParts(value: string | null | undefined, locale: string, now: Date = new Date()): { date: string; time: string } | null {
  if (!value || !Number.isFinite(Date.parse(value))) return null
  const date = new Date(value)
  const year = (value: Date) => formatDate(locale, value, { year: 'numeric', timeZone: undefined })
  const sameYear = year(date) === year(now)
  return {
    date: formatDate(locale, date, { day: '2-digit', month: 'short', ...(sameYear ? {} : { year: 'numeric' }), timeZone: undefined }),
    time: formatTime(locale, date, { hour: '2-digit', minute: '2-digit', timeZone: undefined }),
  }
}

type ScopeCompany = { readonly id: string; readonly name: string; readonly branding?: { readonly monogram?: string | null } | null }
/**
 * Design `udKpis[4]`: the companies' monograms (name when no monogram is recorded). `companies` is undefined while the
 * company list is not loaded (pending or failed): then nothing is labelled (the caller says the information is
 * unavailable). A company id outside the loaded list is counted, never shown as its internal id.
 */
export function scopeCompanyLabels(companyIds: readonly string[], companies: readonly ScopeCompany[] | undefined): { labels: string[]; unknown: number } | null {
  if (!companies) return null
  const labels: string[] = []
  let unknown = 0
  for (const id of companyIds) {
    const company = companies.find(row => row.id === id)
    if (company) labels.push(company.branding?.monogram?.trim() || company.name)
    else unknown++
  }
  return { labels, unknown }
}

type RolePerson = Pick<Personnel, 'roles'> & { readonly configuredSuperAdmin?: boolean | undefined }
/**
 * Roles a person effectively holds. A configured super admin is System_Admin regardless of local assignments
 * (Cosmos:SuperAdmins, R1b) and holds every company (company_scope "*", R1c); the design shows exactly that.
 */
export function effectiveRoles(person: RolePerson): string[] {
  return person.configuredSuperAdmin === true ? [...new Set(['System_Admin', ...person.roles])] : [...person.roles]
}
export function holdsAllCompanies(person: { readonly configuredSuperAdmin?: boolean | undefined }): boolean { return person.configuredSuperAdmin === true }
